import { client } from "../../../api/client";

/**
 * Helpers
 */
const isLocal = () => process.env.REACT_APP_USELOCALDATA === "true";
const baseUri = process.env.REACT_APP_GATEWAY_URI; // e.g. API Gateway base
const apiPath = "iquoteapi/quote"; // maps to Route("api/[controller]") => "api/quote" behind the gateway

/**
 * Fetch all quotes (optionally paginated client-side).
 * When local: reads ./_local/_quotes.json (expects { quotes: [...] }).
 * Returns: { quotes: Quote[], totalRecords: number }
 */
export const fetchQuotes = async (options = {}) => {
    const { pageNumber = 1, pageSize = 25 } = options;

    if (isLocal()) {
        try {
            const all = require("./_local/_quotes.json").quotes || [];
            const startIndex = (pageNumber - 1) * pageSize;
            const endIndex = startIndex + pageSize;
            return { quotes: all.slice(startIndex, endIndex), totalRecords: all.length };
        } catch (error) {
            console.error("Error fetching quotes (local):", error);
            return { quotes: [], totalRecords: 0 };
        }
    } else {
        try {
            // Controller returns a full array; paginate client-side if caller passed options
            const response = await client.get(baseUri, `${apiPath}`);
            const all = Array.isArray(response.data) ? response.data : [];
            const startIndex = (pageNumber - 1) * pageSize;
            const endIndex = startIndex + pageSize;
            return { quotes: all.slice(startIndex, endIndex), totalRecords: all.length };
        } catch (error) {
            console.error("Error fetching quotes:", error);
            return { quotes: [], totalRecords: 0 };
        }
    }
};

/**
 * Fetch single quote by id
 * Returns: Quote | null
 */
export const fetchQuote = async (id) => {
    if (!id && id !== 0) return null;

    if (isLocal()) {
        try {
            const all = require("./_local/_quotes.json").quotes || [];
            return all.find(q => q.id === id) ?? null;
        } catch (error) {
            console.error("Error fetching quote (local):", error);
            return null;
        }
    } else {
        try {
            const response = await client.get(baseUri, `${apiPath}/${id}`);
            console.log("Fetched quote:", id, response.data);
            return response.data ?? null;
        } catch (error) {
            if (error?.response?.status === 404) return null;
            console.error("Error fetching quote:", error);
            return null;
        }
    }
};

/**
 * Create quote
 * Controller: POST api/quote (accepts QuoteDTO)
 * Returns: server response (typically 201) or a mock with newId in local
 */
export const createQuote = async (body) => {
    if (isLocal()) {
        // Simulate created ID for UI flows
        return { data: { newId: Math.floor(Math.random() * 100000) } };
    } else {
        try {
            return await client.post(baseUri, `${apiPath}`, body);
        } catch (error) {
            console.error("Error creating quote:", error);
            return [];
        }
    }
};

/**
 * Update quote
 * Controller: PUT api/quote/{id} (accepts QuoteDTO)
 * Returns: server response or mock success in local
 */
export const updateQuote = async (id, body) => {
    if (isLocal()) {
        // No file writes; pretend success for UI
        return { data: { success: true } };
    } else {
        try {
            console.log("Updating quote with ID:", id, "and body:", body);
            return await client.put(baseUri, `${apiPath}/${id}`, body);
        } catch (error) {
            console.error("Error updating quote:", error);
            return [];
        }
    }
};

/**
 * Delete quote (soft delete on server: sets DeleteDate)
 * Controller: DELETE api/quote/{id}
 * Returns: server response or mock success in local
 */
export const deleteQuote = async (id) => {
    if (isLocal()) {
        // Simulate delete success
        return { data: { success: true } };
    } else {
        try {
            return await client.delete(baseUri, `${apiPath}/${id}`);
        } catch (error) {
            if (error?.response?.status === 404) {
                // Align with controller's NotFound
                return { data: { notFound: true } };
            }
            console.error("Error deleting quote:", error);
            return [];
        }
    }
};

export const fetchQuotesByOpportunityId = async (opportunityId, filter) => {
    if (!opportunityId && opportunityId !== 0) return [];

    if (isLocal()) {
        try {
            const all = require("./_local/_quotes.json").quotes || [];
            return all.filter(q => q.SalesforceOpportunityId === opportunityId);
        } catch (error) {
            console.error("Error fetching quotes by opportunityId (local):", error);
            return [];
        }
    } else {
        try {
            const response = await client.post(baseUri, `${apiPath}/byOpportunity/${opportunityId}`, filter);
            console.log(`Fetched quotes for opportunityId ${opportunityId}:`, response.data);
            return response.data;
        } catch (error) {
            console.error("Error fetching quotes by opportunityId:", error);
            return [];
        }
    }
}

/**
 * Optional: convenience fetch that ignores pagination and just returns an array (server behavior).
 * Useful if you sometimes want the raw list with no wrapper.
 */
export const fetchAllQuotesRaw = async (filter) => {
    if (isLocal()) {
        try {
            return require("./_local/_quotes.json").quotes || [];
        } catch (error) {
            console.error("Error fetching quotes raw (local):", error);
            return [];
        }
    } else {
        try {
            const response = await client.post(baseUri, `${apiPath}/quotes`, filter);
            console.log("Fetched quotes raw:", response.data);
            return response.data;
        } catch (error) {
            console.error("Error fetching quotes raw:", error);
            return [];
        }
    }
};

export const createQuoteDuplicate = async (quoteDetails) => {
    if (isLocal()) {
        return true;
    } else {
        try {
            const response = await client.post(baseUri, `${apiPath}/duplicateQuote`, quoteDetails);
            console.log("Created duplicate quote:", response.data);
            console.log("Duplicated Data:", response.data);
            return { ok: response.ok, data: response.data };
        } catch (error) {
            console.error("Error creating duplicate quote:", error);
            return false;
        }
    }
}